import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface WaiverRequest {
  id: number;
  requested_by: { id: number; full_name: string } | null;
  requested_at: string;
  reason: string;
  supporting_reference: string | null;
  requested_amount: string;
  status: 'pending' | 'approved' | 'rejected';
  decision_amount: string | null;
  decided_by: { id: number; full_name: string } | null;
  decided_at: string | null;
  decision_comment: string | null;
  requires_escalation: boolean;
}

interface PenaltyDetailRecord {
  id: number;
  employer_id: number;
  employer: { id: number; name: string };
  contribution_schedule: { id: number; period_start: string; period_end: string } | null;
  reason_code: string;
  supporting_reference: string | null;
  amount: string;
  paid_to_date: string;
  waived_amount: string;
  due_date: string;
  status: 'assessed' | 'waiver_requested' | 'waived' | 'paid';
  calculation_note: string | null;
  notice_number: string | null;
  receipt_number: string | null;
  collection_status: 'none' | 'reminder_sent' | 'referred_for_recovery';
  assessed_by: { id: number; full_name: string } | null;
  waiver_requests: WaiverRequest[];
}

const STATUS_LABELS: Record<string, string> = {
  assessed: 'Assessed',
  waiver_requested: 'Waiver Requested',
  waived: 'Waived',
  paid: 'Paid',
};

const STATUS_BADGE: Record<string, string> = {
  assessed: 'badge-scheduled',
  waiver_requested: 'badge-neutral',
  waived: 'badge-active',
  paid: 'badge-active',
};

function formatDueDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const COLLECTION_STATUS_LABELS: Record<string, string> = {
  none: 'None',
  reminder_sent: 'Reminder Sent',
  referred_for_recovery: 'Referred for Recovery',
};

export function PenaltyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const { settings, lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [penalty, setPenalty] = useState<PenaltyDetailRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [requestingWaiver, setRequestingWaiver] = useState(false);
  const [waiverForm, setWaiverForm] = useState({ reason: '', supporting_reference: '', requested_amount: '' });

  const [decidingWaiver, setDecidingWaiver] = useState<WaiverRequest | null>(null);
  const [decisionForm, setDecisionForm] = useState({ decision: 'approved' as 'approved' | 'rejected', decision_amount: '', decision_comment: '' });

  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_method: '', amount: '' });

  const isSelfServiceEmployer = Boolean(user?.employer_id);
  const canRequestWaiver = can('penalties.create') || can('employer.penalties.manage');
  const canDecideWaiver = can('penalties.waive') || can('penalties.waive.senior');
  const canReceipt = can('penalties.receipt');
  const canIssueNotice = can('penalties.create');
  const canDelete = can('penalties.archive');
  const canUpdateCollectionStatus = can('penalties.update');

  const fmtMoney = (n: number | string) => formatMoney(Number(n), settings.currency);
  const outstanding = (p: PenaltyDetailRecord) => Number(p.amount) - Number(p.paid_to_date) - Number(p.waived_amount);
  const pendingWaiver = penalty?.waiver_requests.find((w) => w.status === 'pending') ?? null;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<PenaltyDetailRecord>(`/penalties/${id}`);
      setPenalty(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load penalty.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openRequestWaiver = () => {
    if (!penalty) return;
    setWaiverForm({ reason: '', supporting_reference: '', requested_amount: String(outstanding(penalty)) });
    setErrors({});
    setRequestingWaiver(true);
  };

  const submitWaiverRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!penalty) return;
    try {
      await api.post(`/penalties/${penalty.id}/waiver-requests`, waiverForm);
      setRequestingWaiver(false);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const openDecideWaiver = (waiverRequest: WaiverRequest) => {
    setDecisionForm({ decision: 'approved', decision_amount: waiverRequest.requested_amount, decision_comment: '' });
    setErrors({});
    setDecidingWaiver(waiverRequest);
  };

  const submitDecision = async (e: FormEvent) => {
    e.preventDefault();
    if (!decidingWaiver) return;
    try {
      await api.post(`/penalty-waiver-requests/${decidingWaiver.id}/decide`, {
        decision: decisionForm.decision,
        decision_amount: decisionForm.decision === 'approved' ? Number(decisionForm.decision_amount) : undefined,
        decision_comment: decisionForm.decision_comment || undefined,
      });
      setDecidingWaiver(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const openRecordPayment = () => {
    if (!penalty) return;
    setPaymentForm({ payment_method: '', amount: String(outstanding(penalty)) });
    setErrors({});
    setRecordingPayment(true);
  };

  const submitPayment = async (e: FormEvent) => {
    e.preventDefault();
    if (!penalty) return;
    try {
      const res = await api.post<PenaltyDetailRecord>(`/penalties/${penalty.id}/receipt`, {
        payment_method: paymentForm.payment_method,
        amount: Number(paymentForm.amount),
      });
      setRecordingPayment(false);
      showSuccess(res.data.status === 'paid' ? `Fully paid — receipt ${res.data.receipt_number}.` : 'Partial payment recorded.');
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const issueNotice = async () => {
    if (!penalty) return;
    try {
      await api.post(`/penalties/${penalty.id}/notice`);
      showSuccess('Notice issued.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to issue notice.');
    }
  };

  const downloadNotice = () => {
    if (!penalty) return;
    downloadFile(api, `/penalties/${penalty.id}/pdf`, `${penalty.notice_number ?? `penalty-${penalty.id}`}.pdf`);
  };

  const removePenalty = async () => {
    if (!penalty) return;
    if (!(await confirmAction('Delete this penalty?'))) return;
    try {
      await api.delete(`/penalties/${penalty.id}`);
      navigate('/memberships/penalties');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const updateCollectionStatus = async (value: string) => {
    if (!penalty) return;
    try {
      await api.put(`/penalties/${penalty.id}/collection-status`, { collection_status: value });
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Update failed.');
    }
  };

  if (loading) return null;
  if (error || !penalty) {
    return (
      <>
        <header className="page-header">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/penalties')} />
        </header>
        <div className="card">
          <p className="error">{error ?? 'Penalty not found.'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/penalties')} />
          <h1>{penalty.employer.name}</h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${STATUS_BADGE[penalty.status]}`}>{STATUS_LABELS[penalty.status]}</span>
          {canIssueNotice && (
            <button type="button" className="btn btn-ghost" onClick={issueNotice}>
              Issue Notice
            </button>
          )}
          <IconButton icon={IconDownload} label="Download Notice PDF" onClick={downloadNotice} />
          {canReceipt && penalty.status === 'assessed' && (
            <button type="button" className="btn btn-primary" onClick={openRecordPayment}>
              Record Payment
            </button>
          )}
          {canRequestWaiver && penalty.status === 'assessed' && !pendingWaiver && (
            <button type="button" className="btn btn-ghost" onClick={openRequestWaiver}>
              Request Waiver
            </button>
          )}
          {canDelete && penalty.status === 'assessed' && Number(penalty.paid_to_date) === 0 && Number(penalty.waived_amount) === 0 && (
            <button type="button" className="btn btn-danger-ghost" onClick={removePenalty}>
              Delete
            </button>
          )}
        </div>
      </header>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-grid">
          <div>
            <strong>Reason</strong>
            <p>{labelFor(lists, 'penalty_reason_code', penalty.reason_code)}</p>
          </div>
          <div>
            <strong>Amount</strong>
            <p>{fmtMoney(penalty.amount)}</p>
          </div>
          <div>
            <strong>Paid to date</strong>
            <p>{fmtMoney(penalty.paid_to_date)}</p>
          </div>
          <div>
            <strong>Waived</strong>
            <p>{fmtMoney(penalty.waived_amount)}</p>
          </div>
          <div>
            <strong>Outstanding</strong>
            <p>{fmtMoney(outstanding(penalty))}</p>
          </div>
          <div>
            <strong>Due date</strong>
            <p>{formatDueDate(penalty.due_date)}</p>
          </div>
          {penalty.assessed_by && (
            <div>
              <strong>Assessed by</strong>
              <p>{penalty.assessed_by.full_name}</p>
            </div>
          )}
          {penalty.notice_number && (
            <div>
              <strong>Notice number</strong>
              <p>{penalty.notice_number}</p>
            </div>
          )}
          {penalty.receipt_number && (
            <div>
              <strong>Receipt number</strong>
              <p>{penalty.receipt_number}</p>
            </div>
          )}
        </div>
        {penalty.calculation_note && (
          <p className="muted" style={{ marginTop: 12 }}>
            <strong>Basis of calculation:</strong> {penalty.calculation_note}
          </p>
        )}
        {penalty.supporting_reference && (
          <p className="muted">
            <strong>Supporting reference:</strong> {penalty.supporting_reference}
          </p>
        )}
      </div>

      {!isSelfServiceEmployer && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Collection Status</h2>
          {canUpdateCollectionStatus ? (
            <select value={penalty.collection_status} onChange={(e) => updateCollectionStatus(e.target.value)}>
              {Object.entries(COLLECTION_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <p>{COLLECTION_STATUS_LABELS[penalty.collection_status]}</p>
          )}
        </div>
      )}

      <div className="card">
        <h2>Waiver Requests</h2>
        {penalty.waiver_requests.length === 0 ? (
          <p className="muted">No waiver requests yet.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Requested By</th>
                  <th>Reason</th>
                  <th>Requested Amount</th>
                  <th>Status</th>
                  <th>Decided By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {penalty.waiver_requests.map((w) => (
                  <tr key={w.id}>
                    <td>{w.requested_by?.full_name ?? '—'}</td>
                    <td className="muted">{w.reason}</td>
                    <td>{fmtMoney(w.requested_amount)}</td>
                    <td>
                      <span className={`badge ${w.status === 'approved' ? 'badge-active' : w.status === 'rejected' ? 'badge-urgent' : 'badge-neutral'}`}>
                        {w.status}
                      </span>
                      {w.requires_escalation && <small className="muted"> (senior)</small>}
                    </td>
                    <td className="muted">{w.decided_by?.full_name ?? '—'}</td>
                    <td className="right">
                      {canDecideWaiver && w.status === 'pending' && (
                        <button type="button" className="btn btn-ghost" onClick={() => openDecideWaiver(w)}>
                          Decide
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {requestingWaiver && (
        <Modal title="Request a waiver" onClose={() => setRequestingWaiver(false)}>
          <form onSubmit={submitWaiverRequest} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Reason *
              <textarea
                rows={3}
                value={waiverForm.reason}
                onChange={(e) => setWaiverForm({ ...waiverForm, reason: e.target.value })}
                required
              />
              {errors.reason && <small className="error">{errors.reason[0]}</small>}
            </label>
            <label className="span-2">
              Supporting reference
              <input
                value={waiverForm.supporting_reference}
                onChange={(e) => setWaiverForm({ ...waiverForm, supporting_reference: e.target.value })}
              />
            </label>
            <label className="span-2">
              Requested amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={outstanding(penalty)}
                value={waiverForm.requested_amount}
                onChange={(e) => setWaiverForm({ ...waiverForm, requested_amount: e.target.value })}
                required
              />
              {errors.requested_amount && <small className="error">{errors.requested_amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRequestingWaiver(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </Modal>
      )}

      {decidingWaiver && (
        <Modal title="Decide waiver request" onClose={() => setDecidingWaiver(null)}>
          <form onSubmit={submitDecision} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">
              Requested by {decidingWaiver.requested_by?.full_name ?? '—'}: {decidingWaiver.reason}
            </p>
            <label className="span-2">
              Decision *
              <select
                value={decisionForm.decision}
                onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value as 'approved' | 'rejected' })}
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </label>
            {decisionForm.decision === 'approved' && (
              <label className="span-2">
                Decision amount
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={decidingWaiver.requested_amount}
                  value={decisionForm.decision_amount}
                  onChange={(e) => setDecisionForm({ ...decisionForm, decision_amount: e.target.value })}
                />
                {errors.decision_amount && <small className="error">{errors.decision_amount[0]}</small>}
              </label>
            )}
            <label className="span-2">
              Comment
              <textarea
                rows={2}
                value={decisionForm.decision_comment}
                onChange={(e) => setDecisionForm({ ...decisionForm, decision_comment: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setDecidingWaiver(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {recordingPayment && (
        <Modal title="Record payment" onClose={() => setRecordingPayment(false)}>
          <form onSubmit={submitPayment} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">Outstanding balance: {fmtMoney(outstanding(penalty))}</p>
            <label className="span-2">
              Payment method *
              <input
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                required
              />
              {errors.payment_method && <small className="error">{errors.payment_method[0]}</small>}
            </label>
            <label className="span-2">
              Amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={outstanding(penalty)}
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                required
              />
              {errors.amount && <small className="error">{errors.amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRecordingPayment(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
